// @flow

import AppBar from '@mui/material/AppBar';
import style from 'js/components/Prescription/PrescriptionEditor.module.less';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import {
  ArrowBack,
  Block,
  Brush,
  CallSplit,
  Clear,
  CloudDownload,
  Delete,
  Edit,
  Filter2,
  Filter3,
  InfoOutlined,
  MergeType,
  Settings,
  TabUnselected,
} from '@mui/icons-material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CompareIcon from '@mui/icons-material/Compare';
import Button from '@mui/material/Button';
import Badge from '@mui/material/Badge';
import { PRESCRIPTION_OVERRIDES_TOOL, PRESCRIPTION_UNIT } from 'js/constants/PrescriptionConstants';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import React, { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { useLangFile } from 'js/context/LanguageContext';
import { usePrescriptionJob } from 'js/components/Prescription/PrescriptionJobContext';
import PropTypes from 'prop-types';
import PrescriptionAdjustButton from 'js/components/Prescription/Dialogs/PrescriptionAutoAdjustDialog/PrescriptionAdjustButton';
import { MANUAL_FEATURE_FLAGS } from '../../../featureFlags/featureFlags';
import useFirebaseAnalytics, { FIREBASE_EVENTS } from '../../../hooks/useFirebaseAnalytics';
import { useHasFeatures } from '../../../context/PermissionContext';
import FeatureConstants from '../../../constants/FeatureConstants';
import PolygonBufferIcon from '../../Icons/PolygonBufferIcon';
import { Divider, ToggleButtonGroup } from '@mui/material';
import MultipleActionsDialog from '../../Prescription/Dialogs/MultipleActionsDialog.jsx';
import { isSoilLayer, MetaTypes } from '../PrescriptionJob';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getAllUniqueValues, getEffectiveSoilValues } from '../PrescriptionUtils';
import BulkPrescriptionApplyIntervalsButton from '../Dialogs/BulkPrescriptionApplyIntervalsDialog/BulkPrescriptionApplyIntervalsButton';
import BulkPrescriptionReturnToOverviewButton from './Buttons/BulkPrescriptionReturnToOverview';
import NavigateToPreviousFieldButton from './Buttons/NavigateToPreviousFieldButton';
import NavigateToNextFieldButton from './Buttons/NavigateToNextFieldButton';
import useNullZoneIntervalToggle from './useNullZoneIntervalToggle';
import HelpItemContainer from '../../Help/HelpItemContainer';
import { NULL_ZONES_HELP_ARTICLE } from '../../../reducers/HelpReducer';
import { usePrescriptionJobStrategy } from '../PrescriptionJobStrategyContext';
import { usePrescriptionJobOrchestration } from '../SeedingJob/usePrescriptionJobOrchestration';
import { PrescriptionState } from '../PrescriptionState';

const PrescriptionToolbar = (props: PrescriptionToolbar.propTypes) => {
  const analytics = useFirebaseAnalytics();
  const LangFile = useLangFile();
  const allowBufferZones = useHasFeatures([FeatureConstants.PRESCRIPTION_BUFFER_ZONE]);

  const { prescriptionJob } = usePrescriptionJob();
  const prescriptionJobStrategy = usePrescriptionJobStrategy();
  const { prescriptionState } = usePrescriptionJobOrchestration();
  const { hasNullZones, nullZonesEnabled, isOnBulkOverview, toggleNullZones } =
    useNullZoneIntervalToggle(props);

  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const isLastField =
    props.isBulkPrescription &&
    props.bulkPrescriptionFields[props.bulkPrescriptionFields.length - 1].fieldId ===
      prescriptionJob.fieldId;

  const isSplitDisabled = useMemo(
    () =>
      !props.splittingEnable ||
      (isSoilLayer(prescriptionJob.layer) &&
        getAllUniqueValues(
          getEffectiveSoilValues(
            prescriptionJob.values,
            prescriptionJob.layer,
            props.isBulkPrescription && !props.appliedInitialIntervals,
            props.bulkPrescriptionJobs
          )
        ).length === prescriptionJob.intervals.length),
    [
      props.splittingEnable,
      prescriptionJob.layer,
      prescriptionJob.values,
      prescriptionJob.intervals.length,
      props.isBulkPrescription,
      props.appliedInitialIntervals,
      props.bulkPrescriptionJobs,
    ]
  );

  let numberOfOverrides = 0;
  if (prescriptionJob.overrideAreas) {
    numberOfOverrides = Object.keys(prescriptionJob.overrideAreas).length;
  }

  let adjustmentRequired = false;
  if (prescriptionJob.intervals.length > 1) {
    let total, max;
    if (props.isBulkPrescription && !props.appliedInitialIntervals) {
      total = Object.values(props.bulkPrescriptionJobs).reduce(
        (sum, job) => sum + (job.totalPrescription || 0),
        0
      );
      max = Object.values(props.bulkPrescriptionJobs).reduce(
        (sum, job) => sum + (job.maxPrescription || 0),
        0
      );
    } else {
      total = Number(prescriptionJob.totalPrescription);
      max = Number(prescriptionJob.maxPrescription);
    }
    const difference = Math.abs(total - max);
    adjustmentRequired = !(difference < 0.01);
  }

  const onKeyUp = useCallback((event) => {
    if (typeof event.persist === 'function') {
      event.persist();
    }

    if (event.shiftKey) {
      return;
    }

    if (event.key === 'Enter') {
      event.target.blur();
    }
  }, []);

  const selectOnFocus = useCallback((event) => {
    const input = event.target;
    input.setSelectionRange(0, input.value.length);
  }, []);

  const onDownload = useCallback((shouldDownloadMultiple: boolean) => {
    analytics.logEvent(FIREBASE_EVENTS.VRM_EDITOR_DOWNLOAD);
    setShowDownloadConfirm(false);
    props.onDownloadJob(shouldDownloadMultiple);
  });

  const handleDownloadClick = () => {
    if (
      props.hasMultipleJobs ||
      (props.isBulkPrescription && Object.keys(props.bulkFuturePrescriptionJobs || {}).length > 0)
    ) {
      setShowDownloadConfirm(true);
      return;
    }

    onDownload(false);
  };

  const onAddView = useCallback((event) => {
    props.onAddView();
  });

  const onRemoveView = useCallback((event) => {
    props.onRemoveView();
  });

  const onAutoAdjustClicked = useCallback(() => {
    props.onSetShowAutoAdjustDialog(true);
  }, []);

  const handleApplyIntervalsClicked = useCallback(() => {
    props.onApplyIntervalsClicked(true);
  }, []);

  const isLimeJob = prescriptionJob.metaType === MetaTypes.LIME;

  const useHideLabels = (isLimeJob, adjustmentRequired) => {
    const breakpoints = {
      settings: { lime: 1170, adjust: 970, default: 780 },
      download: { lime: 1100, adjust: 900, default: 710 },
      save: { lime: 1030, adjust: 840, default: 650 },
      future: 860,
      limeModel: 930,
    };

    const getBreakpoint = (key) =>
      breakpoints[key]?.[isLimeJob ? 'lime' : adjustmentRequired ? 'adjust' : 'default'];

    return {
      settings: useMediaQuery(`(max-width: ${getBreakpoint('settings')}px)`),
      download: useMediaQuery(`(max-width: ${getBreakpoint('download')}px)`),
      save: useMediaQuery(`(max-width: ${getBreakpoint('save')}px)`),
      future: useMediaQuery(`(max-width: ${breakpoints.future}px)`),
      limeModel: useMediaQuery(`(max-width: ${breakpoints.limeModel}px)`),
    };
  };

  const hideLabels = useHideLabels(isLimeJob, adjustmentRequired);

  let unitString;
  switch (prescriptionJob.unit) {
    case PRESCRIPTION_UNIT.LITER:
      unitString = `${LangFile.PrescriptionSettings.unit.liters.short} / ha`;
      break;
    case PRESCRIPTION_UNIT.PIECES_M2:
      unitString = `${LangFile.PrescriptionSettings.unit.pieces.short}/m\u00B2`;
      break;
    default:
      unitString = `${LangFile.PrescriptionSettings.unit.kilogram.short} / ha`;
      break;
  }

  return (
    <AppBar position="static" color="inherit" className={style.AppBar} elevation={0}>
      <Toolbar className={style.Toolbar}>
        {(props.splitting || props.merging || props.overriding) && (
          <Box className={style.ButtonWrapper}>
            <Tooltip title={LangFile.PrescriptionToolbar.backToZones}>
              <IconButton color="inherit" onClick={props.onReturnToEditor}>
                <ArrowBack />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {!(props.splitting || props.merging || props.overriding) && (
          <Fragment>
            <Box className={style.ButtonWrapper}>
              <Tooltip title={LangFile.PrescriptionToolbar.exit}>
                <IconButton
                  color="inherit"
                  aria-label={LangFile.PrescriptionToolbar.exit}
                  onClick={props.onExitSafe}
                >
                  <Clear />
                </IconButton>
              </Tooltip>
              <Tooltip title={LangFile.PrescriptionToolbar.settingsTooltip}>
                <Box
                  sx={{
                    mr: 1,
                    ml: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    data-testid="open-prescription-settings"
                    aria-label={LangFile.PrescriptionToolbar.settingsTooltip}
                    onClick={props.onShowSettings}
                  >
                    <Settings />
                    <Box
                      sx={{
                        pl: 1,
                        display: hideLabels.settings ? 'none' : 'flex',
                      }}
                    >
                      {LangFile.PrescriptionToolbar.settings}
                    </Box>
                  </Button>
                </Box>
              </Tooltip>
              {isLimeJob && (
                <Tooltip title={LangFile.PrescriptionToolbar.limeModel.tooltip}>
                  <Box
                    sx={{
                      mr: 1,
                      ml: 1,
                    }}
                  >
                    <a
                      href={
                        'https://cropline-assets.s3.eu-central-1.amazonaws.com/Cropline+kalkmodel.pdf'
                      }
                      download="LimeModel"
                    >
                      <Button variant={'outlined'}>
                        <InfoOutlined />
                        <Box
                          sx={{
                            ml: 1,
                            display: hideLabels.limeModel ? 'none' : 'flex',
                          }}
                        >
                          {LangFile.PrescriptionToolbar.limeModel.button}
                        </Box>
                      </Button>
                    </a>
                  </Box>
                </Tooltip>
              )}
              <Tooltip title={LangFile.PrescriptionToolbar.downloadTooltip}>
                <Box
                  sx={{
                    mr: 1,
                    ml: 1,
                  }}
                >
                  <Button
                    variant={'contained'}
                    color={'primary'}
                    aria-label={LangFile.PrescriptionToolbar.downloadTooltip}
                    onClick={handleDownloadClick}
                    disabled={props.isBulkPrescription && !isLastField}
                  >
                    <CloudDownload />
                    <Box
                      sx={{
                        pl: 1,
                        display: hideLabels.download ? 'none' : 'flex',
                      }}
                    >
                      {LangFile.PrescriptionToolbar.download}
                    </Box>
                  </Button>
                </Box>
              </Tooltip>

              <MultipleActionsDialog
                open={showDownloadConfirm}
                title={LangFile.PrescriptionSplitViewContainer.multipleJobs.downloadDialog.title}
                message={
                  LangFile.PrescriptionSplitViewContainer.multipleJobs.downloadDialog.message
                }
                primaryText={
                  LangFile.PrescriptionSplitViewContainer.multipleJobs.downloadDialog.primary
                }
                onPrimary={() => onDownload(true)}
                secondaryText={
                  LangFile.PrescriptionSplitViewContainer.multipleJobs.downloadDialog.secondary
                }
                onSecondary={() => onDownload(false)}
                onCancel={() => setShowDownloadConfirm(false)}
                cancelText={
                  LangFile.PrescriptionSplitViewContainer.multipleJobs.downloadDialog.cancel
                }
              />

              <Tooltip title={LangFile.PrescriptionToolbar.savePrescription.tooltip}>
                <Box
                  sx={{
                    mr: 1,
                    ml: 1,
                  }}
                >
                  <Button
                    variant={'contained'}
                    color={'primary'}
                    onClick={props.onSaveJob}
                    disabled={props.isBulkPrescription && !isLastField}
                  >
                    <SaveOutlinedIcon />
                    <Box
                      sx={{
                        ml: 1,
                        display: hideLabels.save ? 'none' : 'flex',
                      }}
                    >
                      {LangFile.PrescriptionToolbar.savePrescription.button}
                    </Box>
                  </Button>
                </Box>
              </Tooltip>

              {prescriptionState === PrescriptionState.BULK_PRESCRIPTION_OVERVIEW && (
                <BulkPrescriptionApplyIntervalsButton onClick={handleApplyIntervalsClicked} />
              )}

              {!props.overriding &&
                !isLimeJob &&
                (!prescriptionJobStrategy || prescriptionJobStrategy.isAdjustmentNeeded()) &&
                adjustmentRequired && <PrescriptionAdjustButton onClick={onAutoAdjustClicked} />}

              {props.hasMultipleJobs && (
                <Tooltip title={LangFile.PrescriptionToolbar.handleFuturePrescription.tooltip}>
                  <Box
                    sx={{
                      mr: 1,
                      ml: 1,
                    }}
                  >
                    <Button variant={'outlined'} onClick={props.onHandleFutureJobClick}>
                      <CompareIcon />
                      <Box
                        sx={{
                          ml: 1,
                          display: hideLabels.future ? 'none' : 'flex',
                        }}
                      >
                        {LangFile.PrescriptionToolbar.handleFuturePrescription.button}
                      </Box>
                    </Button>
                  </Box>
                </Tooltip>
              )}

              {prescriptionState === PrescriptionState.BULK_PRESCRIPTION_SINGLE_FIELD_VIEW && (
                <>
                  {(!prescriptionJobStrategy ||
                    prescriptionJobStrategy.hasBulkPrescriptionOverview) && (
                    <BulkPrescriptionReturnToOverviewButton
                      onClick={() => props.onShowReturnToBulkOverviewDialog()}
                    />
                  )}
                  <NavigateToPreviousFieldButton
                    onBulkFieldChange={() =>
                      props.onBulkFieldChange(props.bulkPrescriptionFieldIndex - 1)
                    }
                    isDisabled={props.isPreviousButtonDisabled}
                  />
                  <NavigateToNextFieldButton
                    onBulkFieldChange={() =>
                      props.onBulkFieldChange(props.bulkPrescriptionFieldIndex + 1)
                    }
                    isDisabled={props.isNextButtonDisabled}
                  />
                </>
              )}
            </Box>

            <Box className={style.IconButtonWrapper}>
              {props.numberOfAddedViews > 0 ? (
                <Tooltip title={LangFile.PrescriptionToolbar.removeAddedView}>
                  <IconButton
                    color="inherit"
                    aria-label={LangFile.PrescriptionToolbar.removeAddedView}
                    onClick={onRemoveView}
                  >
                    <Filter2 />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={LangFile.PrescriptionToolbar.addNewView}>
                  <IconButton
                    color="inherit"
                    aria-label={LangFile.PrescriptionToolbar.addNewView}
                    onClick={onAddView}
                  >
                    <Filter3 />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={LangFile.PrescriptionToolbar.splitIntervals}>
                <IconButton
                  color="inherit"
                  aria-label={LangFile.PrescriptionToolbar.splitIntervals}
                  disabled={isSplitDisabled}
                  onClick={props.onInitiateSplit}
                >
                  <CallSplit />
                </IconButton>
              </Tooltip>

              <Tooltip title={LangFile.PrescriptionToolbar.mergeIntervals}>
                <IconButton
                  color="inherit"
                  aria-label={LangFile.PrescriptionToolbar.mergeIntervals}
                  disabled={!props.splittingEnable || prescriptionJob.intervals.length < 2}
                  onClick={props.onInitiateMerge}
                >
                  <MergeType />
                </IconButton>
              </Tooltip>

              {hasNullZones && (
                <Box sx={{ position: 'relative', width: 48, height: 48, flex: '0 0 48px' }}>
                  <Tooltip
                    title={
                      isOnBulkOverview
                        ? LangFile.PrescriptionSplitViewContainer.nullZonesBulkTooltip ||
                          'Add or remove a separate interval for null zones in every job'
                        : LangFile.PrescriptionSplitViewContainer.nullZonesTooltip ||
                          'Add or remove a separate interval for null zones in this job'
                    }
                  >
                    <IconButton
                      sx={{ width: 48, height: 48 }}
                      color={nullZonesEnabled ? 'primary' : 'inherit'}
                      aria-label={LangFile.PrescriptionSplitViewContainer.nullZones || 'Null zones'}
                      aria-pressed={nullZonesEnabled}
                      onMouseEnter={() => props.onHighlightNullZones(true)}
                      onMouseLeave={() => props.onHighlightNullZones(false)}
                      onFocus={() => props.onHighlightNullZones(true)}
                      onBlur={() => props.onHighlightNullZones(false)}
                      onClick={toggleNullZones}
                    >
                      <TabUnselected sx={{ transform: 'translateX(-7px)' }} />
                    </IconButton>
                  </Tooltip>
                  <HelpItemContainer type="overlay-icon" articleId={NULL_ZONES_HELP_ARTICLE} />
                </Box>
              )}

              <Tooltip title={LangFile.PrescriptionToolbar.overrideModeTooltip}>
                <IconButton
                  color="inherit"
                  aria-label={LangFile.PrescriptionToolbar.overrideModeTooltip}
                  disabled={props.isBulkPrescription && !props.appliedInitialIntervals}
                  onClick={props.onToggleIsOverriding}
                >
                  <Badge badgeContent={numberOfOverrides} color={'secondary'} overlap={'circular'}>
                    <Edit />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          </Fragment>
        )}

        {props.overriding && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            {(allowBufferZones || MANUAL_FEATURE_FLAGS.PRESCRIPTION_BUFFER_ZONE) && (
              <Box
                className={style.TextFieldBox}
                sx={{
                  minWidth: 100,
                  mt: '-4px',
                }}
              >
                <Box
                  sx={{
                    px: 3,
                    display: 'flex',
                  }}
                >
                  <Divider orientation={'vertical'} flexItem />
                </Box>

                <Button
                  variant={'outlined'}
                  onClick={props.onEditBuffer}
                  startIcon={<PolygonBufferIcon />}
                  className={style.Button}
                >
                  {LangFile.PrescriptionToolbar.buffer}
                </Button>

                <Box
                  sx={{
                    px: 3,
                    display: 'flex',
                  }}
                >
                  <Divider orientation={'vertical'} flexItem />
                </Box>
              </Box>
            )}

            <ToggleButtonGroup
              size="small"
              value={props.tool}
              exclusive
              onChange={props.onToolChanged}
            >
              <ToggleButton value={PRESCRIPTION_OVERRIDES_TOOL.OVERRIDE}>
                <Brush />
              </ToggleButton>
              <ToggleButton value={PRESCRIPTION_OVERRIDES_TOOL.EXCEPTION}>
                <Block />
              </ToggleButton>
              <ToggleButton value={PRESCRIPTION_OVERRIDES_TOOL.ERASER}>
                <Delete />
              </ToggleButton>
            </ToggleButtonGroup>

            {props.tool === PRESCRIPTION_OVERRIDES_TOOL.OVERRIDE && (
              <Box
                className={style.TextFieldBox}
                sx={{
                  width: 100,
                  mt: '-4px',
                  ml: 1,
                }}
              >
                <TextField
                  value={props.overrideBrushValue}
                  InputProps={{
                    endAdornment: unitString,
                  }}
                  onChange={props.onOverrideBrushValueChanged}
                  inputProps={{
                    onFocus: selectOnFocus,
                    onKeyUp: onKeyUp,
                  }}
                  type={'tel'}
                  margin="dense"
                  variant={'outlined'}
                  label={LangFile.PrescriptionToolbar.value}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            )}

            <Box
              className={style.TextFieldBox}
              sx={{
                width: 100,
                mt: '-4px',
                ml: 1,
              }}
            >
              <TextField
                value={props.overrideBrushSize}
                InputProps={{
                  endAdornment: 'm',
                }}
                inputProps={{
                  onFocus: selectOnFocus,
                  onKeyUp: onKeyUp,
                  onBlur: props.onOverrideBrushSizeBlur,
                }}
                onChange={props.onOverrideBrushSizeChanged}
                type={'tel'}
                margin="dense"
                variant={'outlined'}
                label={LangFile.PrescriptionToolbar.size}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

PrescriptionToolbar.propTypes = {
  tool: PropTypes.string,
  merging: PropTypes.bool,
  splitting: PropTypes.bool,
  mergingEnable: PropTypes.bool,
  splittingEnable: PropTypes.bool,
  overriding: PropTypes.bool,
  overrideBrushSize: PropTypes.number,
  overrideBrushValue: PropTypes.number,
  hasMultipleJobs: PropTypes.bool,

  onEditBuffer: PropTypes.any,
  onExitSafe: PropTypes.any,
  onDownloadJob: PropTypes.func,
  onToolChanged: PropTypes.func,
  onShowSettings: PropTypes.func,
  onInitiateSplit: PropTypes.func,
  onInitiateMerge: PropTypes.func,
  onHighlightNullZones: PropTypes.func,
  updatePrescriptionJob: PropTypes.func,
  onReturnToEditor: PropTypes.func,
  onToggleIsOverriding: PropTypes.func,
  onOverrideBrushSizeBlur: PropTypes.func,
  onOverrideBrushSizeChanged: PropTypes.func,
  onOverrideBrushValueChanged: PropTypes.func,
  onSetShowAutoAdjustDialog: PropTypes.func,
  onHandleFutureJobClick: PropTypes.func,
  onSaveJob: PropTypes.func,
  numberOfAddedViews: PropTypes.number,
  onAddView: PropTypes.func,
  onRemoveView: PropTypes.func,
  isBulkPrescription: PropTypes.bool,
  bulkPrescriptionFields: PropTypes.array,
  selectedField: PropTypes.object,
  appliedInitialIntervals: PropTypes.array,
  onApplyIntervalsClicked: PropTypes.func,
  bulkPrescriptionJobs: PropTypes.object,
  bulkFuturePrescriptionJobs: PropTypes.object,
  onShowReturnToBulkOverviewDialog: PropTypes.func,
};

export default memo(PrescriptionToolbar);
